import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { AdminShell } from './AdminShell';
import { adminApi, AdminAuditRow } from '@/lib/adminApi';

export function AdminAuditPage() {
  const { t } = useTranslation();
  const [action, setAction] = useState('');
  const [sinceDays, setSinceDays] = useState<number>(7);

  const list = useQuery({
    queryKey: ['admin', 'audit', { action, sinceDays }],
    queryFn: () =>
      adminApi.listAudit({
        action: action || undefined,
        sinceDays,
        limit: 200,
      }),
    refetchInterval: 30_000,
  });

  const rows: AdminAuditRow[] = list.data?.items ?? [];

  return (
    <AdminShell titleKey="admin.audit.title">
      <p className="text-sm text-ink-600 max-w-2xl mb-6">
        {t('admin.audit.intro')}
      </p>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <input
          type="search"
          placeholder={t('admin.audit.filter_placeholder')}
          value={action}
          onChange={(e) => setAction(e.target.value)}
          className="w-72 rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        />
        <select
          value={sinceDays}
          onChange={(e) => setSinceDays(Number(e.target.value))}
          className="rounded-md border border-ink-300 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-accent-500/30"
        >
          <option value={1}>{t('admin.audit.last_24h')}</option>
          <option value={7}>{t('admin.audit.last_7d')}</option>
          <option value={30}>{t('admin.audit.last_30d')}</option>
          <option value={90}>{t('admin.audit.last_90d')}</option>
        </select>
        <span className="text-xs text-ink-500 num">
          {t('admin.audit.row_count', { count: rows.length })}
        </span>
      </div>

      {list.isLoading && (
        <p className="text-sm text-ink-500">{t('common.loading')}</p>
      )}

      {!list.isLoading && rows.length === 0 && (
        <div className="rounded-2xl border border-dashed border-ink-300 p-10 text-center text-ink-500 max-w-3xl">
          <p className="text-sm">{t('admin.audit.empty')}</p>
        </div>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm bg-white rounded-2xl border border-ink-200">
            <thead className="text-left text-ink-500 border-b border-ink-200">
              <tr>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_action')}
                </th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_target')}
                </th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_company')}
                </th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_user')}
                </th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_time')}
                </th>
                <th className="py-2.5 px-3 font-medium">
                  {t('admin.audit.col_payload')}
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-ink-100">
                  <td className="py-2.5 px-3 font-medium text-ink-900 num">
                    {r.action}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-700">
                    {r.target_kind || '—'}
                    {r.target_id && (
                      <span className="text-ink-400">
                        #{r.target_id.slice(0, 8)}
                      </span>
                    )}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {r.company_id?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {r.user_id?.slice(0, 8) ?? '—'}
                  </td>
                  <td className="py-2.5 px-3 num text-ink-500 text-xs">
                    {new Date(r.occurred_at).toLocaleString()}
                  </td>
                  <td className="py-2.5 px-3 text-ink-500 text-xs max-w-xs truncate">
                    {Object.keys(r.payload || {}).length > 0
                      ? JSON.stringify(r.payload).slice(0, 80)
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </AdminShell>
  );
}
